import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiClient as defaultClient, type ApiClient, type OrgSummary } from '../api/client';
import { useAuth } from '../auth/context';
import { OrgContext, type OrgContextValue, type OrgStatus } from './context';

export type { OrgContextValue, OrgStatus } from './context';

export interface OrgProviderProps {
  children: ReactNode;
  /** Injectable client (tests). */
  client?: ApiClient;
  /** Seed state (tests/guards). */
  initialStatus?: OrgStatus;
  initialOrgs?: OrgSummary[];
}

/**
 * Tracks the signed-in user's org memberships. Loads on the auth transition to
 * `authenticated`; clears on `anonymous`. A failed load sets `error` and is NEVER
 * conflated with "no orgs" — guards must distinguish an empty list (route to
 * create-org) from an error (show a retryable error UI).
 */
export function OrgProvider({
  children,
  client = defaultClient,
  initialStatus = 'idle',
  initialOrgs = [],
}: OrgProviderProps) {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<OrgStatus>(initialStatus);
  const [orgs, setOrgs] = useState<OrgSummary[]>(initialOrgs);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const list = await client.listOrgs();
      setOrgs(list);
      setStatus('ready');
      return list;
    } catch (err) {
      setError(err);
      setStatus('error');
      throw err;
    }
  }, [client]);

  const addOrg = useCallback((created: OrgSummary) => {
    setOrgs((prev) => (prev.some((o) => o.id === created.id) ? prev : [...prev, created]));
    setError(null);
    setStatus('ready');
  }, []);

  useEffect(() => {
    if (authStatus === 'authenticated') {
      // Swallow here: the rejection is reflected in the `error`/`status` state,
      // which the RequireOrg guard renders (with retry).
      void refresh().catch(() => {});
    } else if (authStatus === 'anonymous') {
      setOrgs([]);
      setError(null);
      setStatus('idle');
    }
  }, [authStatus, refresh]);

  const currentOrg = orgs[0] ?? null;

  const value = useMemo<OrgContextValue>(
    () => ({ status, orgs, currentOrg, error, refresh, addOrg }),
    [status, orgs, currentOrg, error, refresh, addOrg],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
